
import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Clock, Plus, CalendarDays, Users, AlertTriangle, Loader2 } from "lucide-react";

export default function Projects() {
  const { isAuthenticated } = useAuth();
  const { projects, setCurrentProject, isLoading, error } = useProject();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Calculate project progress
  const calculateProgress = (project: any) => {
    const totalStages = 8; // RIBA stages 0-7
    const completedStages = project.stages?.filter((s: any) => s.status === "completed").length || 0;
    const currentStageProgress = project.stages?.[project.currentStage]?.progress || 0;
    
    const completeWeight = completedStages / totalStages;
    const currentWeight = 1 / totalStages * (currentStageProgress / 100);
    
    return Math.round((completeWeight + currentWeight) * 100);
  };

  // Days remaining calculation
  const getDaysRemaining = (endDate: Date) => {
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Handle project selection
  const handleSelectProject = (project: any) => {
    setCurrentProject(project);
    navigate("/dashboard");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-1 container py-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading projects...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <NavBar />
        <main className="flex-1 container py-6">
          <div className="text-center py-12">
            <div className="bg-red-50 rounded-lg p-8 max-w-md mx-auto">
              <h3 className="text-xl font-medium mb-2 text-red-900">Error Loading Projects</h3>
              <p className="text-red-700 mb-6">{error.message}</p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      
      <main className="flex-1 container py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
            <p className="text-gray-500">Manage your construction projects</p>
          </div>
          <Link to="/new-project">
            <Button>
              <Plus className="mr-2 h-4 w-4" /> New Project
            </Button>
          </Link>
        </div>
        
        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
              <h3 className="text-xl font-medium mb-2">No projects yet</h3>
              <p className="text-gray-500 mb-6">Create your first project to get started with the RIBA workflow</p>
              <Link to="/new-project">
                <Button>
                  <Plus className="mr-2 h-4 w-4" /> Create First Project
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="overflow-hidden">
                <div className="h-3 bg-primary w-full"></div>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold">{project.name}</h3>
                    <Badge variant={project.currentStage <= 2 ? "outline" : "default"}>
                      Stage {project.currentStage}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{project.client}</p>
                  
                  <div className="mt-4">
                    <div className="flex justify-between items-center text-sm mb-1">
                      <span>Progress</span>
                      <span>{calculateProgress(project)}%</span>
                    </div>
                    <Progress value={calculateProgress(project)} />
                  </div>
                  
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-2" />
                      <span>
                        {getDaysRemaining(project.targetCompletionDate)} days remaining
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarDays className="h-4 w-4 mr-2" />
                      <span>
                        Started {project.startDate.toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      <span>
                        {project.risks?.filter((r: any) => r.status === "open").length || 0} open risks
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-2" />
                      <span>{project.team?.length || 0} team members</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="bg-gray-50 px-6 py-3">
                  <Button 
                    variant="default" 
                    className="w-full"
                    onClick={() => handleSelectProject(project)}
                  >
                    View Project
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
